import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { realtimeService } from '../services/realtimeService';
import useSoundNotifications from '../hooks/useSoundNotifications';
import useDesktopNotifications from '../hooks/useDesktopNotifications';

const RealtimeContext = createContext({});

export const useRealtime = () => {
  const context = useContext(RealtimeContext);
  if (!context) {
    throw new Error('useRealtime must be used within RealtimeProvider');
  }
  return context;
};

export const RealtimeProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [toastNotifications, setToastNotifications] = useState([]);
  const [latestNotification, setLatestNotification] = useState(null);
  const [latestMessage, setLatestMessage] = useState(null);
  const [latestReview, setLatestReview] = useState(null);
  const [showPushPrompt, setShowPushPrompt] = useState(false);
  const promptShownRef = useRef(false);

  const { isMuted, toggleMute, playSound } = useSoundNotifications();
  const {
    isEnabled: desktopEnabled,
    isSupported: desktopSupported,
    permission: desktopPermission,
    requestPermission: requestDesktopPermission,
    showNotification,
    toggleDesktopNotifications
  } = useDesktopNotifications();

  // Auto-request push permission once per user session after login
  useEffect(() => {
    if (!user?.id) return;
    if (!desktopSupported) return;
    if (desktopPermission === 'granted' || desktopPermission === 'denied') return;
    if (promptShownRef?.current) return;

    const dismissedKey = `pushPromptDismissed:${user?.id}`;
    const alreadyDismissed = localStorage.getItem(dismissedKey);
    if (alreadyDismissed) return;

    // Show prompt banner after 3 seconds
    const timer = setTimeout(() => {
      setShowPushPrompt(true);
      promptShownRef.current = true;
    }, 3000);

    return () => clearTimeout(timer);
  }, [user?.id, desktopSupported, desktopPermission]);

  // Handle push prompt accept
  const handleAcceptPushPrompt = useCallback(async () => {
    setShowPushPrompt(false);
    await requestDesktopPermission();
  }, [requestDesktopPermission]);

  // Handle push prompt dismiss
  const handleDismissPushPrompt = useCallback(() => {
    setShowPushPrompt(false);
    if (user?.id) {
      localStorage.setItem(`pushPromptDismissed:${user?.id}`, 'true');
    }
  }, [user?.id]);

  // Add toast notification
  const addToast = useCallback((notification) => {
    const id = Date?.now();
    setToastNotifications(prev => [...prev, { ...notification, id }]);
    
    // Auto-remove after 5 seconds
    setTimeout(() => {
      removeToast(id);
    }, 5000);
  }, []);

  // Remove toast notification
  const removeToast = useCallback((id) => {
    setToastNotifications(prev => prev?.filter(toast => toast?.id !== id));
  }, []);

  // Determine notification icon/sound by type
  const getNotifMeta = useCallback((type) => {
    switch (type) {
      case 'match': case'listing_match': case'wishlist_match':
        return { sound: 'match', icon: '🎯', pushIcon: '/favicon.ico' };
      case 'message':
        return { sound: 'message', icon: '💬', pushIcon: '/favicon.ico' };
      case 'community':
        return { sound: 'notification', icon: '📣', pushIcon: '/favicon.ico' };
      case 'review':
        return { sound: 'notification', icon: '⭐', pushIcon: '/favicon.ico' };
      case 'transaction':
        return { sound: 'notification', icon: '🤝', pushIcon: '/favicon.ico' };
      default:
        return { sound: 'notification', icon: '🔔', pushIcon: '/favicon.ico' };
    }
  }, []);

  // Handle notification updates (matches, community, general)
  const handleNotificationUpdate = useCallback((payload) => {
    if (payload?.eventType === 'INSERT') {
      setLatestNotification(payload?.new);
      setUnreadNotifications(prev => prev + 1);
      
      const title = payload?.new?.title || 'New Notification';
      const message = payload?.new?.message || 'You have a new notification';
      const notifType = payload?.new?.type;
      const meta = getNotifMeta(notifType);

      // Play sound
      playSound(meta.sound);

      // Browser push notification (fires when tab is backgrounded)
      showNotification(title, {
        body: message,
        icon: meta.pushIcon,
        tag: `notification-${payload?.new?.id}`,
        data: { type: notifType, relatedId: payload?.new?.relatedId }
      });

      // In-app toast
      addToast({
        type: 'notification',
        title,
        message,
        icon: meta.icon
      });
    } else if (payload?.eventType === 'UPDATE' && payload?.new?.isRead) {
      setUnreadNotifications(prev => Math?.max(0, prev - 1));
    } else if (payload?.eventType === 'DELETE') {
      setUnreadNotifications(prev => Math?.max(0, prev - 1));
    }
  }, [addToast, playSound, showNotification, getNotifMeta]);

  // Handle message updates
  const handleMessageUpdate = useCallback((payload) => {
    if (payload?.eventType === 'INSERT') {
      setLatestMessage(payload?.new);
      setUnreadMessages(prev => prev + 1);

      playSound('message');

      // Browser push notification for new messages
      showNotification('New Message', {
        body: payload?.new?.content
          ? (payload?.new?.content?.length > 80
              ? payload?.new?.content?.substring(0, 80) + '…'
              : payload?.new?.content)
          : 'You have received a new message',
        icon: '/favicon.ico',
        tag: `message-${payload?.new?.id}`,
        data: { type: 'message', conversationId: payload?.new?.conversationId }
      });
      
      addToast({
        type: 'message',
        title: 'New Message',
        message: payload?.new?.content
          ? (payload?.new?.content?.length > 60
              ? payload?.new?.content?.substring(0, 60) + '…'
              : payload?.new?.content)
          : 'You have received a new message',
        icon: '💬'
      });
    }
  }, [addToast, playSound, showNotification]);

  // Handle review updates
  const handleReviewUpdate = useCallback((payload) => {
    if (payload?.eventType === 'INSERT') {
      setLatestReview(payload?.new);

      playSound('notification');

      showNotification('New Review', {
        body: `You received a ${payload?.new?.rating}-star review`,
        icon: '/favicon.ico',
        tag: `review-${payload?.new?.id}`,
        data: { type: 'review' }
      });
      
      addToast({
        type: 'review',
        title: 'New Review',
        message: `You received a ${payload?.new?.rating}-star review`,
        icon: '⭐'
      });
    }
  }, [addToast, playSound, showNotification]);

  // Handle transaction updates
  const handleTransactionUpdate = useCallback((payload) => {
    if (payload?.eventType === 'INSERT') {
      playSound('notification');
      showNotification('New Trade Offer', {
        body: 'Someone has made a trade offer with you',
        icon: '/favicon.ico',
        tag: `transaction-${payload?.new?.id}`,
        data: { type: 'transaction' }
      });
      addToast({
        type: 'transaction',
        title: 'New Trade Offer',
        message: 'Someone has made a trade offer with you',
        icon: '🤝'
      });
    } else if (payload?.eventType === 'UPDATE' && payload?.new?.status !== payload?.old?.status) {
      const statusMessages = {
        completed: 'Trade completed successfully! 🎉',
        disputed: 'A transaction has been disputed',
        cancelled: 'Transaction has been cancelled',
        resolved: 'Transaction dispute has been resolved'
      };
      
      const body = statusMessages?.[payload?.new?.status] || 'Transaction status updated';
      playSound('notification');
      showNotification('Transaction Update', {
        body,
        icon: '/favicon.ico',
        tag: `transaction-update-${payload?.new?.id}`,
        data: { type: 'transaction' }
      });
      addToast({
        type: 'transaction',
        title: 'Transaction Update',
        message: body,
        icon: '🤝'
      });
    }
  }, [addToast, playSound, showNotification]);

  // Subscribe to real-time updates (notifications covers matches + community + general)
  useEffect(() => {
    if (!user?.id) return;

    const unsubscribe = realtimeService?.subscribeToMultiple(user?.id, {
      notifications: handleNotificationUpdate,
      messages: handleMessageUpdate,
      reviews: handleReviewUpdate,
      transactions: handleTransactionUpdate
    });

    return () => unsubscribe();
  }, [user?.id, handleNotificationUpdate, handleMessageUpdate, handleReviewUpdate, handleTransactionUpdate]);

  const value = {
    unreadNotifications,
    unreadMessages,
    latestNotification,
    latestMessage,
    latestReview,
    toastNotifications,
    addToast,
    removeToast,
    setUnreadNotifications,
    setUnreadMessages,
    // Sound controls
    isMuted,
    toggleMute,
    playSound,
    // Desktop notification controls
    desktopEnabled,
    desktopSupported,
    desktopPermission,
    requestDesktopPermission,
    toggleDesktopNotifications,
    // Push prompt
    showPushPrompt,
    handleAcceptPushPrompt,
    handleDismissPushPrompt
  };

  return (
    <RealtimeContext.Provider value={value}>
      {children}
      {/* Browser push permission prompt banner */}
      {showPushPrompt && (
        <div
          role="dialog"
          aria-label="Enable push notifications"
          className="fixed bottom-20 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-sm bg-card border border-border rounded-2xl shadow-xl p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 duration-300"
        >
          <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-lg">
            🔔
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground">Stay in the loop</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Get instant alerts for new matches, messages, and community updates — even when this tab is in the background.
            </p>
            <div className="flex gap-2 mt-3">
              <button
                onClick={handleAcceptPushPrompt}
                className="flex-1 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:bg-primary/90 transition-colors"
              >
                Enable notifications
              </button>
              <button
                onClick={handleDismissPushPrompt}
                className="px-3 py-1.5 text-muted-foreground text-xs font-medium rounded-lg hover:bg-muted transition-colors"
              >
                Not now
              </button>
            </div>
          </div>
        </div>
      )}
    </RealtimeContext.Provider>
  );
};